import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, MapPin, Users, Edit,
  UserCircle, Mic, Image as ImageIcon,
  ExternalLink, X, Repeat2, Copy, CheckCircle, Code,
  Trash, RotateCw, MessageSquare, Save, Mail, Building2,
  HandCoins, UserCheck
} from 'lucide-react';
import { eventAPI, eventCodeAPI, merchantAPI } from '../../../services/api';
import { showToast } from '../../../utils/toasts';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import QRCodeDisplay from '../../../components/events/QRCodeDisplay';
import SmsAutomationSettings from '../../../components/events/SmsAutomationSettings';
import { format } from 'date-fns';
import PermissionGuard from '../../../components/guards/PermissionGuard';

const EventDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const isServiceRoute = pathname.startsWith('/services/');

  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [attendance, setAttendance] = useState<any>(null);
  const [allEventCodes, setAllEventCodes] = useState<any[]>([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [regeneratingCodes, setRegeneratingCodes] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);

  // SMS Automation Settings
  const [smsAutomation, setSmsAutomation] = useState({
    enabled: false,
    notifications: [],
    externalRecipients: []
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [givingEnabled, setGivingEnabled] = useState(false);
  const [togglingGiving, setTogglingGiving] = useState(false);
  const [showGivingQR, setShowGivingQR] = useState(false);
  const [givingErrorModal, setGivingErrorModal] = useState(false);
  const [smsAutomationStatus, setSmsAutomationStatus] = useState<{
    hasRunToday: boolean;
    lastRun?: string;
    nextScheduledRun?: string;
  } | null>(null);

  // Feature Access State
  const [features, setFeatures] = useState<{
    smsAutomation: boolean;
    eventDonations: boolean;
  }>({
    smsAutomation: false,
    eventDonations: false
  });

  useEffect(() => {
    fetchEvent();
    fetchUserPlanFeatures();
  }, [id]);

  const fetchUserPlanFeatures = async () => {
    try {
      const response = await merchantAPI.planFeatures();
      if (response?.data?.success) {
        const data = response.data;
        if (data.data?.features) {
          setFeatures({
            smsAutomation: data.data.features.smsAutomation || false,
            eventDonations: data.data.features.eventDonations || false
          });
        }
      }
    } catch (error) {
      // Silently fail - features will default to false
      console.log('Could not fetch plan features');
    }
  };

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await eventAPI.getEvent(id!);
      const eventData = response.data.data?.event;
      const attendanceData = response.data.data?.stats?.attendance;
      setEvent(eventData);
      setAttendance(attendanceData?.totalAttended || 0);
      setGivingEnabled(eventData.giving?.enabled || false);

      // Load SMS automation settings
      if (eventData.smsAutomation) {
        setSmsAutomation({
          enabled: eventData.smsAutomation.enabled || false,
          notifications: eventData.smsAutomation.notifications || [],
          externalRecipients: eventData.smsAutomation.externalRecipients || []
        });
      }

      // If recurring event, fetch today's event code
      if (eventData.isRecurring) {
        fetchAllEventCodes(eventData._id);
      }

      // Check SMS Automation Status
      if (eventData.smsAutomation?.enabled && eventData.smsAutomation?.notifications?.length > 0) {
        // Determine if automation has run today by checking if any notification was sent
        const today = new Date().toDateString();
        const lastNotificationDate = eventData.smsAutomation.lastNotificationSent 
          ? new Date(eventData.smsAutomation.lastNotificationSent).toDateString()
          : null;
        
        setSmsAutomationStatus({
          hasRunToday: lastNotificationDate === today,
          lastRun: eventData.smsAutomation.lastNotificationSent,
          nextScheduledRun: eventData.smsAutomation.nextScheduledRun
        });
      }

    } catch (error: any) {
      showToast.error('Failed to load event details');
      navigate(isServiceRoute ? '/services' : '/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEventCodes = async (eventId: string) => {
    try {
      const response = await eventCodeAPI.getCodesForEvent(eventId);
      if (response.status === 200 && response.data.success) {
        setAllEventCodes(response.data.data || []);
      }
    } catch (error) {
      // Silently fail
      console.log(error)
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await eventAPI.deleteEvent(id!);
      showToast.success('Event deleted successfully');
      navigate(isServiceRoute ? '/services' : '/events');
    } catch (error: any) {
      showToast.error('Failed to delete event');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleRegenerateQR = async () => {
    try {
      await eventAPI.regenerateQR(id!);
      await fetchEvent(); 
      showToast.success('QR Code regenerated successfully');
    } catch (error: any) {
      showToast.error('Failed to regenerate QR code');
    } 
  };

  const handleRegenerateEventCodes = async () => {
    try {
      setRegeneratingCodes(true);
      setShowRegenerateModal(false);
      const response = await eventCodeAPI.regenerateCodes(id!);
      await fetchAllEventCodes(id!);
      showToast.success(response.data.message || 'Event codes regenerated successfully');
    } catch (error: any) {
      showToast.error('Failed to regenerate event codes');
    } finally {
      setRegeneratingCodes(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    showToast.success('Code copied to clipboard');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);

      await eventAPI.updateEvent(id!, {
        smsAutomation
      });

      showToast.success('Settings updated successfully');
      await fetchEvent(); // Reload event data to get any backend-generated fields like publicUrl
    } catch (error: any) {
      showToast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGivingToggle = async () => {
    const next = !givingEnabled;
    setTogglingGiving(true);
    try {
      await eventAPI.updateEvent(id!, { givingEnabled: next });
      setGivingEnabled(next);
      await fetchEvent();
      showToast.success(next ? 'Public giving link enabled' : 'Public giving link disabled');
    } catch (err: any) {
      if (err?.response?.data?.error === 'BRANCH_PAYOUT_REQUIRED') {
        setGivingErrorModal(true);
      } else {
        showToast.error(err?.response?.data?.message || 'Failed to update giving settings');
      }
    } finally {
      setTogglingGiving(false);
    }
  };

  const getGivingUrl = () =>
    event?.giving?.uniqueId
      ? `${window.location.origin}/give/${event.giving.uniqueId}`
      : null;

  const getCheckInUrl = () => {
    if (event.isRecurring) {
      return `${window.location.origin}/attend/${event._id}`;
    } else {
      return `${window.location.origin}/events/attend/${event.qrCode?.data}`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const scheduleText = (() => {
    if (!event) return '';
    const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const ORD: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', [-1]: 'Last' };
    const fmtTime = (t: string) => {
      if (!t) return '';
      const [h, m] = t.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hr = h % 12 || 12;
      return m === 0 ? `${hr}:00 ${ampm}` : `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
    };

    if (!event.isRecurring) {
      return event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1);
    }

    const r = event.recurrence || {};
    const time = r.baseTime ? ` - ${fmtTime(r.baseTime)}` : '';

    if (r.frequency === 'daily') return `Daily${time}`;

    if (r.frequency === 'weekly') {
      const names = [...(r.daysOfWeek || [])].sort().map((d: number) => DAY[d]);
      if (!names.length) return `Weekly${time}`;
      if (names.length === 1) return `${names[0]}s${time}`;
      const last = names.pop();
      return `${names.join(', ')} & ${last}s${time}`;
    }

    if (r.frequency === 'monthly') {
      if (r.monthlyType === 'relative' && r.monthlyOrdinal != null && r.monthlyWeekday != null) {
        const ord = ORD[r.monthlyOrdinal] ?? `${r.monthlyOrdinal}th`;
        return `${ord} ${DAY[r.monthlyWeekday]} of the month${time}`;
      }
      const day = r.daysOfWeek?.[0];
      if (day) {
        const sfx = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
        return `${day}${sfx} of the month${time}`;
      }
      return `Monthly${time}`;
    }

    if (r.frequency === 'yearly') {
      if (r.yearlyMonth && r.yearlyDay) {
        return `${MONTH[r.yearlyMonth - 1]} ${r.yearlyDay}${time}`;
      }
      return `Yearly${time}`;
    }

    return event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading {isServiceRoute ? 'Service' : 'Event'}...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">Event not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 ">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <button
            onClick={() => navigate(isServiceRoute ? '/services' : '/events')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-3 sm:mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to {isServiceRoute ? 'Services' : 'Events'}</span>
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Mobile: title, then status/public/date on one row below */}
              <div className="sm:hidden">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {event.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                    {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </span>
                  {event.isPublic && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                      Public
                    </span>
                  )}
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {scheduleText}
                  </span>
                </div>
              </div>

              {/* Desktop: title + badges inline, date below */}
              <div className="hidden sm:block">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {event.title}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                    {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </span>
                  {event.isPublic && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                      Public
                    </span>
                  )}
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {scheduleText}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <PermissionGuard permission="events.edit">
              <button
                onClick={() => navigate(`/${event.eventType === 'service' ? 'services' : 'events'}/${id}/edit`)}
                className="hidden sm:flex px-3 py-2 sm:px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg items-center space-x-1 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              </PermissionGuard>
              <button
                onClick={() => navigate(isServiceRoute ? '/attendance' : `/events/${id}/attendance`)}
                className="hidden sm:flex px-3 py-2 sm:px-4 text-sm sm:text-base bg-primary-600 hover:bg-primary-700 text-white rounded-lg items-center space-x-1 transition-colors"
              >
                <span>Attendance</span>
                <p>({attendance})</p>
              </button>
              {event.registration?.enabled && (
                <button
                  onClick={() => navigate(`/events/${id}/registrations`)}
                  className="px-3 py-2 sm:px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-1 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Registrations</span>
                </button>
              )}
              <PermissionGuard permission="events.delete">
              <button
                onClick={() => {
                  setShowDeleteModal(true);
                }}
                className="hidden sm:flex p-2.5 bg-red-600 hover:bg-red-700 rounded-lg items-center transition-colors"
              >
                <Trash className="w-5 h-5 text-white dark:text-white" />
              </button>
              </PermissionGuard>

            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Cover Image */}
            {event.coverImage?.url && (
              <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                <img
                  src={event.coverImage.url}
                  alt={event.title}
                  className="w-full h-48 sm:h-96 object-cover cursor-pointer hover:opacity-95 transition-opacity"
                  onClick={() => setSelectedImage(event.coverImage.url)}
                />
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  About This Event
                </h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}

            {/* Gallery */}
            {event.images && event.images.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5" />
                  <span>Event Gallery</span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {event.images.map((image: any, index: number) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.url}
                        alt={image.caption || `Gallery image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                        onClick={() => setSelectedImage(image.url)}
                      />
                      {image.caption && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {image.caption}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hosts */}
            {event.hosts && event.hosts.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Event Hosts</span>
                </h2>
                <div className="space-y-4">
                  {event.hosts.map((host: any, index: number) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      {host.type === 'member' && host.member?.photo ? (
                        <img
                          src={host.member.photo}
                          alt={`${host.member.firstName} ${host.member.lastName}`}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                          <UserCircle className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {host.type === 'member'
                            ? `${host.member.firstName} ${host.member.lastName}`
                            : host.externalHost.name
                          }
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {host.type === 'member'
                            ? 'Church Member'
                            : host.externalHost.title
                          }
                        </p>
                        {host.type === 'external' && host.externalHost.organization && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            {host.externalHost.organization}
                          </p>
                        )}
                        {host.type === 'external' && host.externalHost.bio && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                            {host.externalHost.bio}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Speakers */}
            {event.speakers && event.speakers.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                  <Mic className="w-5 h-5" />
                  <span>Event Speakers</span>
                </h2>
                <div className="space-y-4">
                  {event.speakers.map((speaker: any, index: number) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      {speaker.type === 'member' && speaker.member?.photo ? (
                        <img
                          src={speaker.member.photo}
                          alt={`${speaker.member.firstName} ${speaker.member.lastName}`}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                          <UserCircle className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {speaker.type === 'member'
                            ? `${speaker.member.firstName} ${speaker.member.lastName}`
                            : speaker.externalSpeaker.name
                          }
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {speaker.type === 'member'
                            ? 'Church Member'
                            : speaker.externalSpeaker.title
                          }
                        </p>
                        {speaker.type === 'external' && speaker.externalSpeaker.organization && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            {speaker.externalSpeaker.organization}
                          </p>
                        )}
                        {speaker.type === 'external' && speaker.externalSpeaker.topic && (
                          <div className="mt-2 px-3 py-1 bg-primary-100 dark:bg-primary-900 rounded-full inline-block">
                            <p className="text-xs text-primary-700 dark:text-primary-300 font-medium">
                              Topic: {speaker.externalSpeaker.topic}
                            </p>
                          </div>
                        )}
                        {speaker.type === 'external' && speaker.externalSpeaker.bio && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                            {speaker.externalSpeaker.bio}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Event Codes & Check-In Link (Recurring Events) */}
            {event.isRecurring && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                    <Code className="w-5 h-5 text-primary-600" />
                    <span>Check-In Codes</span>
                  </h2>
                  <button
                    onClick={() => setShowRegenerateModal(true)}
                    disabled={regeneratingCodes}
                    className="p-2 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={regeneratingCodes ? 'Regenerating...' : 'Regenerate all event codes'}
                  >
                    <RotateCw className={`w-5 h-5 text-orange-600 ${regeneratingCodes ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {allEventCodes.length > 0 ? (
                  <div className="space-y-4">
                    {/* Mobile Card List */}
                    <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      {allEventCodes.map((codeObj: any) => {
                        const now = new Date();
                        const validFrom = new Date(codeObj.validFrom);
                        const validUntil = new Date(codeObj.validUntil);
                        const isActive = now >= validFrom && now <= validUntil;
                        const isPast = now > validUntil;

                        return (
                          <div key={codeObj._id} className="p-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-mono font-bold text-gray-900 dark:text-gray-100">{codeObj.code}</p>
                                {isActive ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                    Active
                                  </span>
                                ) : isPast ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                    Expired
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-blue-300">
                                    Upcoming
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {format(new Date(codeObj.serviceDate), 'MMM d, yyyy')} · {format(validFrom, 'h:mm a')} - {format(validUntil, 'h:mm a')}
                              </p>
                            </div>
                            <button
                              onClick={() => handleCopyCode(codeObj.code)}
                              className="p-2 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 rounded-lg transition-colors flex-shrink-0"
                              title="Copy code"
                            >
                              {copiedCode ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <Copy className="w-5 h-5 text-primary-600" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-primary-50 dark:bg-primary-900/20">
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Date</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Code</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Opens</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Closes</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Status</th>
                              <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allEventCodes.map((codeObj: any) => {
                              const now = new Date();
                              const validFrom = new Date(codeObj.validFrom);
                              const validUntil = new Date(codeObj.validUntil);
                              const isActive = now >= validFrom && now <= validUntil;
                              const isPast = now > validUntil;

                              return (
                                <tr key={codeObj._id} className="border-b border-gray-200 dark:border-gray-700">
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{format(new Date(codeObj.serviceDate), 'MMM d, yyyy')}</td>
                                  <td className="px-4 py-3 font-mono font-bold text-lg text-gray-900 dark:text-gray-100">{codeObj.code}</td>
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{format(validFrom, 'h:mm a')}</td>
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{format(validUntil, 'h:mm a')}</td>
                                  <td className="px-4 py-3">
                                    {isActive ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                        Active
                                      </span>
                                    ) : isPast ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                        Expired
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-blue-300">
                                        Upcoming
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <button
                                      onClick={() => handleCopyCode(codeObj.code)}
                                      className="p-2 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
                                      title="Copy code"
                                    >
                                      {copiedCode ? (
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                      ) : (
                                        <Copy className="w-5 h-5 text-primary-600" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No event codes have been generated for this event yet.
                  </p>
                )}
              </div>
            )}

            {/* SMS Automation Settings */}
            {!features.smsAutomation ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 relative opacity-60">
                <div className="flex flex-col items-center justify-between mb-6">
                  <div className="inset-0 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-50 dark:bg-opacity-50 rounded-xl">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-3">
                        <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">SMS Automations</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Upgrade your plan to access SMS Automation</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
              {/* SMS Automation Status Banner */}
              {smsAutomation?.enabled && smsAutomationStatus?.hasRunToday && (
                <div className={`rounded-lg p-4 border mb-6 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800`}>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className={`w-5 h-5 mt-0.5 text-primary-600 dark:text-primary-400`} />
                    <div className="flex-1">
                      <p className={`text-sm font-bold text-primary-900 dark:text-primary-100`}>
                        Automation Already Ran Today
                      </p>
                      <p className={`text-sm mt-1 text-primary-700 dark:text-blue-300`}>
                        {smsAutomationStatus.lastRun ? (
                          <>
                            Last run:{' '}
                            <span className="font-semibold uppercase">
                              {new Date(smsAutomationStatus.lastRun).toLocaleString([], { 
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                            {' '}
                            <span className="font-medium">
                              If you've made changes to automations, you'll need to manually trigger new messages.
                            </span>
                          </>
                        ) : (
                          <>
                            SMS notifications were sent today.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  <MessageSquare className="w-5 h-5 text-primary-600" />
                  <span>SMS Automation</span>
                </h2>
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center space-x-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {savingSettings ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
              <SmsAutomationSettings
                value={smsAutomation}
                onChange={setSmsAutomation}
              />
            </div>
            )}

            {/* ── Public Giving ──────────────────────────────────────── */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                    <HandCoins className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Public Giving</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Shareable offering link for this event</p>
                  </div>
                </div>

                {/* Toggle */}
                {togglingGiving ? (
                  <span className="text-sm text-gray-400">Saving…</span>
                ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={givingEnabled}
                      onChange={handleGivingToggle}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" />
                  </label>
                )}
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                {!givingEnabled ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    Enable to generate a shareable link and QR code that members can use to give their offerings for this event.
                  </p>
                ) : getGivingUrl() ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Share this link with your congregation so they can give their tithe or offering for <strong className="text-gray-800 dark:text-gray-200">{event.title}</strong>.
                    </p>

                    {/* URL row */}
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={getGivingUrl()!}
                        className="flex-1 min-w-0 px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300"
                      />
                      <button
                        onClick={() => { navigator.clipboard.writeText(getGivingUrl()!); showToast.success('Link copied!'); }}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                        title="Copy link"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-3 gap-3">
                      {/* QR Code */}
                      <button
                        onClick={() => setShowGivingQR(true)}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Code className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">QR Code</span>
                      </button>
                      {/* WhatsApp */}
                      <button
                        onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Give your tithe or offering for ${event.title}: ${getGivingUrl()}`)}`, '_blank')}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      >
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span className="text-xs font-medium text-green-700 dark:text-green-400">WhatsApp</span>
                      </button>
                      {/* Preview */}
                      <button
                        onClick={() => window.open(getGivingUrl()!, '_blank')}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        <span className="text-xs font-medium text-primary-700 dark:text-primary-400">Preview</span>
                      </button>
                    </div>

                    {/* Stats */}
                    {(event.giving?.totalRaised > 0 || event.giving?.givingCount > 0) && (
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="text-center py-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            GHS {(event.giving?.totalRaised || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Total raised</p>
                        </div>
                        <div className="text-center py-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {event.giving?.givingCount || 0}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Payments</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 animate-pulse">Generating link…</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* QR Code */}
            {event.qrCode?.imageUrl && (
              <QRCodeDisplay
                qrCodeUrl={event.qrCode.imageUrl}
                eventTitle={event.title}
                eventId={event._id}
                qrData={event.qrCode.data}
                qrUrl={getCheckInUrl()}
                onRegenerate={handleRegenerateQR}
              />
            )}

            {/* Registration URL */}
            {event.registration?.enabled && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Event Registration
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Share this link with members to allow them to register for the event.
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/events/register/${event._id}`}
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400"
                  />
                  <button
                    onClick={() => {
                      const registrationUrl = `${window.location.origin}/events/register/${event._id}`;
                      navigator.clipboard.writeText(registrationUrl);
                      showToast.success('Registration link copied to clipboard');
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Copy registration link"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      const registrationUrl = `${window.location.origin}/events/register/${event._id}`;
                      const message = encodeURIComponent(`Check out this event and register here: ${registrationUrl}`);
                      window.open(`https://wa.me/?text=${message}`, '_blank');
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Share via WhatsApp"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const registrationUrl = `${window.location.origin}/events/register/${event._id}`;
                      const subject = encodeURIComponent(`You're invited: ${event.title}`);
                      const body = encodeURIComponent(`You're invited to ${event.title}.\n\nRegister here: ${registrationUrl}`);
                      window.location.href = `mailto:?subject=${subject}&body=${body}`;
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Share via Email"
                  >
                    <Mail className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Date & Time */}
            {!event.isRecurring && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Date & Time
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {event.eventDate && !isNaN(new Date(event.eventDate).getTime())
                          ? format(new Date(event.eventDate), 'EEEE, MMMM d, yyyy')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Time</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {event.startTime ? (() => {
                          const [hours, minutes] = event.startTime.split(':');
                          const hour = parseInt(hours);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const displayHour = hour % 12 || 12;
                          const startTimeFormatted = `${displayHour}:${minutes} ${ampm}`;
                          if (event.endTime && event.endTime !== 'Invalid date') {
                            const [endHours, endMinutes] = event.endTime.split(':');
                            const endHour = parseInt(endHours);
                            const endAmpm = endHour >= 12 ? 'PM' : 'AM';
                            const endDisplayHour = endHour % 12 || 12;
                            const endTimeFormatted = `${endDisplayHour}:${endMinutes} ${endAmpm}`;
                            return `${startTimeFormatted} - ${endTimeFormatted}`;
                          }
                          return startTimeFormatted;
                        })() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recurrence (if recurring) */}
            {event.isRecurring && event.recurrence && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 border-t-4 border-t-primary-600">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                  <Repeat2 className="w-5 h-5 text-primary-600" />
                  <span>Recurring Pattern</span>
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Frequency</p>
                    <p className="text-gray-900 dark:text-gray-100 font-medium capitalize">
                      {event.recurrence.frequency}
                    </p>
                  </div>
                  {event.recurrence.daysOfWeek && event.recurrence.daysOfWeek.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Days</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {event.recurrence.daysOfWeek.map((day: number) => {
                          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          return (
                            <span
                              key={day}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                            >
                              {dayNames[day]}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Start Time</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {event.recurrence.baseTime ? (() => {
                          const [hours, minutes] = event.recurrence.baseTime.split(':');
                          const hour = parseInt(hours);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const displayHour = hour % 12 || 12;
                          return `${displayHour}:${minutes} ${ampm}`;
                        })() : 'N/A'}
                      </p>
                    </div>
                    {event.recurrence.baseEndTime && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">End Time</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {(() => {
                            const [hours, minutes] = event.recurrence.baseEndTime.split(':');
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const displayHour = hour % 12 || 12;
                            return `${displayHour}:${minutes} ${ampm}`;
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Start Date</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {event.recurrence.startDate && !isNaN(new Date(event.recurrence.startDate).getTime())
                          ? format(new Date(event.recurrence.startDate), 'MMM d, yyyy')
                          : 'N/A'}
                      </p>
                    </div>
                    {event.recurrence.endDate && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">End Date</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium">
                          {event.recurrence.endDate && !isNaN(new Date(event.recurrence.endDate).getTime())
                            ? format(new Date(event.recurrence.endDate), 'MMM d, yyyy')
                            : 'N/A'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Branch & Location */}
            {(event.branch || event.location?.venue || event.location?.address) && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>Branch & Location</span>
                </h2>
                <div className="space-y-4">
                  {event.branch && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Branch</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{event.branch.name}</p>
                      </div>
                    </div>
                  )}
                  {(event.location?.venue || event.location?.address) && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Venue</p>
                        {event.location?.venue && (
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{event.location.venue}</p>
                        )}
                        {event.location?.address && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {[
                              event.location.address.street,
                              event.location.address.city,
                              event.location.address.state,
                              event.location.address.country
                            ].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-6 h-6 text-gray-900 dark:text-gray-100" />
          </button>
          <img
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={`Delete ${isServiceRoute ? 'Service' : 'Event'}`}
        message={`Are you sure you want to delete "${event.title}"? This will permanently remove the ${isServiceRoute ? 'service' : 'event'} and all associated data including attendance records, check-ins, registrations, and SMS logs. This action cannot be undone.`}
        type="danger"
        isLoading={deleting}
      />

      {/* Regenerate Event Codes Modal */}
      <ConfirmModal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onConfirm={handleRegenerateEventCodes}
        title="Regenerate Event Codes"
        message={`Are you sure you want to regenerate event codes for "${event.title}"? This will delete all existing codes for upcoming dates and generate new ones. Anyone with old codes will need the new codes to check in.`}
        type="warning"
        isLoading={regeneratingCodes}
      />

      {/* Branch Payout Required Modal */}
      {givingErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <HandCoins className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                    Branch Payout Account Required
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    This event's branch doesn't have a payout account configured. You need to set up a
                    bank or Mobile Money account for this branch before enabling Public Giving.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                    Once configured, all giving payments for this event will settle directly into that branch's account.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setGivingErrorModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
                <a
                  href="/settings?tab=branch-payouts"
                  onClick={() => setGivingErrorModal(false)}
                  className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold text-center hover:bg-primary-700 transition-colors"
                >
                  Set Up Branch Payout
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Giving QR Code Modal */}
      {showGivingQR && getGivingUrl() && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-primary-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Giving QR Code</h3>
              </div>
              <button
                onClick={() => setShowGivingQR(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* QR image */}
            <div className="flex flex-col items-center px-6 py-6 gap-4">
              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(getGivingUrl()!)}&margin=8`}
                  alt="Giving QR Code"
                  className="w-64 h-64"
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{event.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">Scan to give tithe or offering</p>
              </div>

              {/* Download + Copy URL */}
              <div className="w-full flex gap-3">
                <a
                  href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(getGivingUrl()!)}&margin=10`}
                  download={`giving-qr-${event.giving?.uniqueId}.png`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Download
                </a>
                <button
                  onClick={() => { navigator.clipboard.writeText(getGivingUrl()!); showToast.success('Link copied!'); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Floating Attendance Button */}
      <button
        onClick={() => navigate(isServiceRoute ? '/attendance' : `/events/${id}/attendance`)}
        className="sm:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-colors"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
        title={`Attendance (${attendance})`}
      >
        <UserCheck className="w-6 h-6" />
      </button>
    </div>
  );
};

export default EventDetails;